import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useClickOutside } from '../../hooks/useClickOutside'; // Adjusted path
import { FilterIcon } from '../icons/FilterIcon'; // Assuming a FilterIcon exists

// Styled Components
const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    background-color: #282E33; // Similar to other inputs
    border: 1px solid #3B444C;
    border-radius: 8px;
    padding: 0; // Padding will be on button and input
    position: relative; // For popover positioning
    width: 100%;
    max-width: 400px; // Or as per design
    &:focus-within {
        border-color: #579DFF;
    }
`;

const FilterButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #3B444C; // Slightly different to distinguish
    color: #B6C2CF;
    border: none;
    padding: 10px 12px;
    border-radius: 8px 0 0 8px; // Rounded on the left
    cursor: pointer;
    font-size: 13px; // Smaller text for filter name
    white-space: nowrap;
    min-width: 80px; // To accommodate "Filter" or short names

    svg {
        margin-right: 6px;
        width: 14px;
        height: 14px;
    }

    &:hover {
        background-color: #4A525A;
    }
`;

const SearchInputStyled = styled.input`
    flex-grow: 1;
    background-color: transparent; // Blends with SearchContainer
    color: #B6C2CF;
    border: none;
    padding: 10px 12px;
    font-size: 14px;
    border-radius: 0 8px 8px 0; // Rounded on the right

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: #7A828A;
    }
`;

const FilterPopover = styled.div`
    position: absolute;
    top: 100%; // Position below the container
    left: 0;
    background-color: #282E33;
    border: 1px solid #3B444C;
    border-radius: 0 0 8px 8px; // Rounded bottom
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 100;
    width: 100%; // Same width as SearchContainer
    margin-top: 2px; // Small gap
`;

const FilterOption = styled.div`
    padding: 10px 15px;
    color: #B6C2CF;
    cursor: pointer;
    font-size: 14px;

    &:hover {
        background-color: #3B444C;
    }

    &.active {
        background-color: #579DFF;
        color: white;
    }
`;

// Default filter options if not provided
const defaultFilterOptions = ["All", "Title", "Description", "Checklist", "URL"];

const CombinedSearchFilter = ({
    searchQuery,
    setSearchQuery,
    searchFilter,
    setSearchFilter,
    filterOptions = defaultFilterOptions // Use default if prop not passed
}) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const popoverRef = useRef(null);

    useClickOutside(popoverRef, () => {
        if (isPopoverOpen) {
            setIsPopoverOpen(false);
        }
    });

    const handleFilterButtonClick = () => {
        setIsPopoverOpen(!isPopoverOpen);
    };

    const handleFilterOptionClick = (option) => {
        // Assuming filterOptions are like "Title", "Description"
        // and searchFilter state expects lowercase values like "title", "description"
        setSearchFilter(option.toLowerCase());
        setIsPopoverOpen(false);
    };

    const handleInputChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Capitalize first letter for display
    const displayFilterName = searchFilter.charAt(0).toUpperCase() + searchFilter.slice(1);

    return (
        <SearchContainer ref={popoverRef}>
            <FilterButton onClick={handleFilterButtonClick}>
                <FilterIcon />
                {displayFilterName}
            </FilterButton>
            <SearchInputStyled
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleInputChange}
            />
            {isPopoverOpen && (
                <FilterPopover>
                    {filterOptions.map((option) => (
                        <FilterOption
                            key={option}
                            onClick={() => handleFilterOptionClick(option)}
                            className={searchFilter === option.toLowerCase() ? 'active' : ''}
                        >
                            {option}
                        </FilterOption>
                    ))}
                </FilterPopover>
            )}
        </SearchContainer>
    );
};

export default CombinedSearchFilter;
